import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, BellSimple, Check, ArrowsLeftRight, Envelope, ClipboardText } from '@phosphor-icons/react';

import { InlineSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_META = {
  lead_assigned:               { color: '#10B981', bg: '#D1FAE5', icon: ArrowsLeftRight, label: 'Lead Assigned' },
  lead_reassigned_away:        { color: '#F59E0B', bg: '#FEF3C7', icon: ArrowsLeftRight, label: 'Lead Reassigned Away' },
  lead_reassigned_cross_region:{ color: '#EF4444', bg: '#FEE2E2', icon: ArrowsLeftRight, label: 'Cross-Region Reassign' },
  memo_issued:                 { color: '#7C2D12', bg: '#FED7AA', icon: ClipboardText, label: 'Memo Issued' },
  memo_acknowledgement:        { color: '#10B981', bg: '#D1FAE5', icon: ClipboardText, label: 'Memo Update' },
};

const MyNotifications = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread

  const fetchData = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/notifications/mine`, { params: { unread_only: filter === 'unread' } });
      setItems(resp.data.items || []);
      setUnread(resp.data.unread_count || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markRead = async (id) => {
    try { await axios.post(`${API}/notifications/${id}/read`); fetchData(); }
    catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try { await axios.post(`${API}/notifications/read-all`); fetchData(); }
    catch { /* ignore */ }
  };

  const onClickItem = (n) => {
    if (!n.read) markRead(n.id);
    if (n.data?.lead_id) navigate(`/leads/${n.data.lead_id}`);
    else if (n.data?.memo_id) navigate(`/memos`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="my-notifications-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="my-notifications-title">
            My Notifications
          </h1>
          <p className="text-[#6D8E87]">Updates on leads, reassignments and account events</p>
        </div>
        {unread > 0 && (
          <button onClick={markAllRead}
            className="flex items-center gap-2 px-5 py-2.5 border border-[#00a69c] text-[#00a69c] rounded-full text-sm font-medium hover:bg-[#E2F3F0] transition"
            data-testid="mark-all-read-btn">
            <Check size={16} weight="bold" /> Mark all read ({unread})
          </button>
        )}
      </div>

      <div className="flex gap-1 p-1 bg-white border border-[#D5E8E4] rounded-full inline-flex w-fit mb-4">
        {[{ v: 'all', l: 'All' }, { v: 'unread', l: `Unread${unread > 0 ? ` (${unread})` : ''}` }].map(opt => (
          <button key={opt.v} onClick={() => setFilter(opt.v)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${filter === opt.v ? 'bg-[#00a69c] text-white' : 'text-[#3B5A54]'}`}
            data-testid={`notif-filter-${opt.v}`}>
            {opt.l}
          </button>
        ))}
      </div>

      {loading ? (
        <InlineSkeleton />
      ) : items.length === 0 ? (
        <div className="medical-card p-12 text-center" data-testid="no-notifications">
          <BellSimple size={48} className="mx-auto text-[#D5E8E4] mb-3" />
          <p className="text-[#6D8E87] text-lg">{filter === 'unread' ? "You're all caught up!" : 'No notifications yet.'}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {items.map(n => {
            const meta = TYPE_META[n.type] || { color: '#00a69c', bg: '#E2F3F0', icon: Bell, label: 'Notification' };
            const Icon = meta.icon;
            return (
              <div key={n.id} onClick={() => onClickItem(n)}
                className={`medical-card p-4 cursor-pointer hover:shadow-md transition ${!n.read ? 'border-l-4 border-l-[#00a69c]' : ''}`}
                data-testid={`notification-${n.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: meta.bg, color: meta.color }}>
                    <Icon size={20} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h3 className="text-sm font-semibold text-[#052A23] truncate">{n.title}</h3>
                      <span className="text-[10px] text-[#6D8E87] shrink-0">
                        {new Date(n.created_at).toLocaleString('en', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm text-[#3B5A54]">{n.body}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide" style={{ backgroundColor: meta.bg, color: meta.color }}>
                        {meta.label}
                      </span>
                      {!n.read && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-semibold">NEW</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyNotifications;
